import { Table } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { SectionCard, ToggleButton } from '@themes/aegix';
import type { ProfileNotificationItem } from '../types';

/**
 * The three notification preferences both apps expose today. Exported so an
 * adapter can reuse the canonical set; pass a custom `items` array to override.
 */
export const DEFAULT_NOTIFICATION_ITEMS: ProfileNotificationItem[] = [
  {
    id: 'toggle_alert_drills_and_updates_push_notification',
    label: 'Alerts, drills, & updates',
    editable: false,
  },
  {
    id: 'toggle_initial_direct_chat_and_mentions_push_notification',
    label: 'Initial direct chats & @mentions',
    editable: true,
  },
  {
    id: 'toggle_all_chats_push_notification',
    label: 'All chats',
    editable: true,
  },
];

export interface ProfileNotificationsSectionProps {
  /** Defaults to {@link DEFAULT_NOTIFICATION_ITEMS}. */
  items?: ProfileNotificationItem[];
  /** Current on/off state keyed by item id. */
  values: Record<string, boolean>;
  onToggle: (id: string, next: boolean) => void;
  title?: string;
  e2eTitle?: string;
}

/**
 * Notification preference toggles. Presentational: state + persistence live in
 * the adapter (which owns the user mutation + error toast).
 */
export function ProfileNotificationsSection({
  items = DEFAULT_NOTIFICATION_ITEMS,
  values,
  onToggle,
  title,
  e2eTitle,
}: ProfileNotificationsSectionProps) {
  const { t } = useTranslation();
  return (
    <SectionCard title={title ?? t('notification', 'Notifications')} e2eTitle={e2eTitle}>
      <Table.Root>
        <Table.Body>
          {items.map(({ id, label, editable }) => (
            <Table.Row key={id} bg="surface.primary">
              <Table.Cell>{t(id, t(`placeholders.${id}`, label))}</Table.Cell>
              <Table.Cell textAlign="right">
                <ToggleButton
                  disabled={!editable}
                  e2eTitle={id}
                  checked={!!values[id]}
                  onCheckedChange={(details: { checked: boolean }) =>
                    onToggle(id, details.checked)
                  }
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </SectionCard>
  );
}

export default ProfileNotificationsSection;
